// Iter 82.3 / Phase E1.3 — Approval Dashboard (HR + Manager scoped).
// KPI cards: Total Pending, Avg Approval Time, Longest Pending,
// Pending > 3 days, Pending > 7 days. Plus pending-by-role bars and
// throughput-by-week chart and top blockers.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClockCountdown, Hourglass, Warning, Speedometer, ChartLine } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

function Kpi({ icon, label, value, suffix = "", tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
    };
    return (
        <div className={`rounded-xl border p-4 ${map[tone]}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
                {icon}
            </div>
            <div className="text-3xl font-bold mt-2">
                {value}{suffix && <span className="text-sm font-normal opacity-70 ml-1">{suffix}</span>}
            </div>
        </div>
    );
}

export default function LeaveApprovalDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/leave/report/approval-dashboard");
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load dashboard"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);

    if (loading || !data) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="approval-dashboard-loading">
                <PageHeader eyebrow="Leave Reports" title="Approval Dashboard" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }

    const maxByRole = Math.max(1, ...Object.values(data.pending_by_role || {}));
    const maxWeekly = Math.max(1, ...data.throughput_by_week.map((w) => w.count));

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="approval-dashboard-page">
            <PageHeader
                eyebrow="Leave Reports"
                title="Approval Dashboard"
                description={`KPIs and funnel for the leave approval pipeline · scope: ${data.scope}`}
            />

            <section className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ad-kpis">
                <Kpi testid="ad-kpi-pending" icon={<ClockCountdown size={18} />} label="Total Pending" value={data.total_pending} tone="amber" />
                <Kpi testid="ad-kpi-avg" icon={<Speedometer size={18} />} label="Avg Approval Time" value={data.avg_approval_days.toFixed(1)} suffix="days" tone="sky" />
                <Kpi testid="ad-kpi-longest" icon={<Hourglass size={18} />} label="Longest Pending" value={data.longest_pending_days.toFixed(1)} suffix="days" tone="slate" />
                <Kpi testid="ad-kpi-3d" icon={<Warning size={18} />} label="Pending > 3 Days" value={data.pending_gt_3_days} tone="orange" />
                <Kpi testid="ad-kpi-7d" icon={<Warning size={18} />} label="Pending > 7 Days" value={data.pending_gt_7_days} tone="rose" />
            </section>

            {data.longest_pending_request && (
                <section className="mt-4 bg-white rounded-xl border border-slate-200 p-4" data-testid="ad-longest">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-semibold text-slate-800 mb-1">Longest Pending Request</h3>
                            <div className="text-xs text-slate-600">
                                <Link to={`/leave/requests/${data.longest_pending_request.id}`} className="font-mono text-sky-700 hover:underline">
                                    {data.longest_pending_request.id}
                                </Link>{" "}
                                · {data.longest_pending_request.employee_name} · {data.longest_pending_request.leave_type_code} · {data.longest_pending_request.days_total} days · {formatDate(data.longest_pending_request.from_date)} → {formatDate(data.longest_pending_request.to_date)}
                            </div>
                        </div>
                        <Badge className="bg-rose-100 text-rose-800">{data.longest_pending_request.age_days?.toFixed(1)} days waiting</Badge>
                    </div>
                </section>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ad-by-role">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Pending by Stage Role</h3>
                    {Object.keys(data.pending_by_role).length === 0 ? (
                        <div className="text-sm text-slate-400 py-6 text-center">No pending requests.</div>
                    ) : (
                        <ul className="space-y-2">
                            {Object.entries(data.pending_by_role).map(([role, count]) => (
                                <li key={role} className="text-xs" data-testid={`ad-role-${role}`}>
                                    <div className="flex justify-between mb-0.5">
                                        <span className="text-slate-700">{role.replace(/_/g, " ")}</span>
                                        <span className="font-semibold">{count}</span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded overflow-hidden">
                                        <div className="h-full bg-amber-500" style={{ width: `${(count / maxByRole) * 100}%` }} />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ad-throughput">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <ChartLine size={14} /> Weekly Throughput ({data.fiscal_year_window_days} days · {data.decided_in_window} decisions)
                    </h3>
                    {data.throughput_by_week.length === 0 ? (
                        <div className="text-sm text-slate-400 py-6 text-center">No decisions in this window.</div>
                    ) : (
                        <div className="flex items-end gap-2 h-32">
                            {data.throughput_by_week.map((w) => (
                                <div key={w.week} className="flex-1 flex flex-col items-center" data-testid={`ad-week-${w.week}`}>
                                    <div className="text-[10px] text-slate-500 mb-1">{w.count}</div>
                                    <div className="bg-emerald-500 rounded-t w-full" style={{ height: `${(w.count / maxWeekly) * 100}%` }} />
                                    <div className="text-[9px] text-slate-400 mt-1 transform -rotate-45 origin-left whitespace-nowrap">{w.week}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {data.top_blockers.length > 0 && (
                <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="ad-blockers">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Top Blockers</h3>
                    <div className="flex flex-wrap gap-2">
                        {data.top_blockers.map((b) => (
                            <Badge key={b.key} variant="outline" className="text-xs" data-testid={`ad-blocker-${b.key}`}>
                                {b.key.replace(/_/g, " ")}: <strong className="ml-1">{b.count}</strong>
                            </Badge>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
